import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { forwardRef, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Button from '@base/Button/Button';
import { register } from '@actions/summit';
import FreePlanUnlock from '@comp/FreePlanUnlock/FreePlanUnlock';
import { useDispatchToStore } from '@hooks/reduxHooks';
import { sendEvents } from '@events/sendAnalytics';
import { segment } from '@actions/analytics';
import { SummitRegister } from '@events/events';
import { LearnerId, LearnerUserName } from '@events/eventProps';
import { GTM_TAP_ENROLL } from '@constants/segment';
import { unlockFreePlanFor } from '@constants/index';
import { useIsMobile } from '@stores/AppCommonData';
import { Spacings, Fonts } from '@styles/index';
import { scrollToRef } from './sections';

const StyledButton = styled(Button)`
  margin-top: ${Spacings.SPACING_4B};
  width: auto;
  height: auto;
  color: ${({ $isRegistered }) =>
    $isRegistered ? 'var(--color-i-blue)' : 'var(--color-base-0)'};
  background-color: ${({ $isRegistered }) =>
    $isRegistered ? 'transparent' : 'var(--color-i-blue)'};
  border: ${({ $isRegistered }) =>
    $isRegistered ? '2px solid var(--color-i-blue)' : 'none'};
  border-radius: ${Spacings.SPACING_2B};
  font-size: ${Fonts.H4};
  font-weight: ${Fonts.SEMIBOLD};
  line-height: 25px;
  padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_8B};
  &:active {
    background-color: ${({ $isRegistered }) =>
      $isRegistered ? 'transparent' : 'var(--color-i-blue)'};
    border: ${({ $isRegistered }) =>
      $isRegistered ? '2px solid var(--color-i-blue)' : 'none'};
  }
`;

const RegisterButton = forwardRef(({ className, scheduleRef }, ref) => {
  const isMobileDevice = useIsMobile();
  const dispatch = useDispatchToStore();
  const { query } = useRouter();
  const { summitKey } = query;

  const [freePlanUnlockDrawerState, setFreePlanUnlockDrawerState] =
    useState(false);
  const closeFreePlanUnlockDrawer = () => setFreePlanUnlockDrawerState(false);
  const [registerClicked, setRegisterClicked] = useState(false);

  const isRegistered = useSelector(
    ({ summit }) => summit.isRegistered || false
  );
  const summitUid = useSelector(({ summit }) => summit.info?.uid || '');
  const isLoggedIn = useSelector(({ auth }) => !!auth.me?.uid || false);
  const userUid = useSelector(({ auth }) => auth.me?.uid || '');
  const username = useSelector(({ auth }) => auth.me?.username || '');
  const [showWatchEvents, setShowWatchEvents] = useState(false);
  const schedule = useSelector(({ summit }) => summit.schedule);
  const lastClassTime = schedule[schedule.length - 1]?.data
    ?.map((el) => el.live_at)
    .sort((a, b) => new Date(b) - new Date(a))[0];
  useEffect(() => {
    let watchEvents = isRegistered;
    if (!watchEvents) {
      watchEvents = new Date(lastClassTime) < new Date();
    }
    setShowWatchEvents(watchEvents);
  }, [lastClassTime, isRegistered]);

  const registerSummit = () => {
    dispatch(register(summitUid)).then((res) => {
      if (res?.result?.success_code) {
        const eventData = {
          [LearnerId]: userUid || 'anonymous',
          [LearnerUserName]: username || 'anonymous',
          'Summit Uid': summitUid,
          'Summit Name': summitKey?.toLowerCase() || ''
        };
        sendEvents(SummitRegister, eventData, true);
        segment.track(SummitRegister, eventData);
        segment.track(GTM_TAP_ENROLL, eventData);
      } else {
        setRegisterClicked(true);
      }
    });
  };

  useEffect(() => {
    if (isLoggedIn && registerClicked) {
      registerSummit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const onClick = () => {
    if (showWatchEvents) {
      scrollToRef(scheduleRef, isMobileDevice);
    } else {
      registerSummit();
    }
  };
  return (
    <>
      <StyledButton
        className={className}
        label={showWatchEvents ? 'Watch events' : 'Register now'}
        $isRegistered={showWatchEvents}
        onClick={onClick}
        ref={ref}
      />
      <FreePlanUnlock
        drawerState={freePlanUnlockDrawerState}
        closeDrawerHandler={closeFreePlanUnlockDrawer}
        objectType={unlockFreePlanFor.SUMMIT}
        objectUid={summitUid}
        callBack={() => {
          setTimeout(() => setFreePlanUnlockDrawerState(false), 3000);
          registerSummit();
        }}
      />
    </>
  );
});

RegisterButton.displayName = 'RegisterButton';

RegisterButton.propTypes = {
  className: PropTypes.string,
  scheduleRef: PropTypes.objectOf(PropTypes.object)
};

RegisterButton.defaultProps = {
  className: '',
  scheduleRef: null
};

export default RegisterButton;
